/* eslint-disable max-lines */
import { CommonModule, ViewportScroller } from '@angular/common';
import { Component, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import dayjs from 'dayjs';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { Observable, exhaustMap, first, skipWhile, tap, timer } from 'rxjs';
import { FinalReport } from '@bn/api-interfaces';
import {
  AppShieldEventLogsComponent,
  AppShieldPricingTableComponent,
  DocumentationComponent,
  SHIELD_PATHS,
  ShieldService,
  ShieldZoneResponse,
  WAFExecutionModeEnum,
} from '@bn/app-shield';
import { PlanType } from '@bn/app-shield/enums';
import { UserStateService } from '@bn/auth/stores';
import { ThemeService } from '@bn/dashboard-shared/services';
import { PullZoneApiService } from '@bn/dashboard-shared/services/pull-zone';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { UserTheme } from '@bn/shared/entities/enums';
import { ContextType, StringBooleanMap, StringNumberMap } from '@bn/shared/entities/models';
import { ContentService } from '@bn/shared/services';
import { StoreRequest } from '@bn/shared/state-store';
import { DAYJS_FORMAT, DateTimeUtils } from '@bn/shared/utils';
import { AlertComponent } from '@bn/web-ui/alert/alert.component';
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { ChartDataGranularity } from '@bn/web-ui/chart';
import { ChartVolumeOfAttacksComponent } from '../../components/chart-volume-of-attacks/chart-volume-of-attacks.component';
import { DatePickerComponent } from '@bn/web-ui/datepicker/datepicker.component';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog';
import { FlagComponent } from '@bn/web-ui/flag/flag.component';
import { LinkListItem } from '@bn/web-ui/link-list/models';
import { LoadingComponent } from '@bn/web-ui/loading/loading.component';
import { ListWithIconItem, PremiumBannerTitle } from '@bn/web-ui/premium-banner';
import { PremiumBannerComponent } from '@bn/web-ui/premium-banner/premium-banner.component';
import { StatCardComponent } from '@bn/web-ui/stat-card/stat-card.component';
import { AppShieldEventLogsStore, ShieldAccessListsApiStore, ShieldApiStore, ShieldUploadScanningApiStore } from '../../stores';
import { MapShieldOverviewChartsData, ShieldOverviewChartData } from '../../stores/helpers/shield-api-store.helper';

@Component({
  selector: 'app-shield-overview-page',
  templateUrl: './app-shield-overview.page.html',
  styleUrls: ['./app-shield-overview.page.scss'],
  providers: [AppShieldEventLogsStore],
  imports: [
    AppShieldEventLogsComponent,
    AppShieldPricingTableComponent,
    CommonModule,
    DocumentationComponent,
    RouterLink,
    AlertComponent,
    ButtonComponent,
    CardComponent,
    DatePickerComponent,
    FlagComponent,
    LoadingComponent,
    PremiumBannerComponent,
    StatCardComponent,
    ChartVolumeOfAttacksComponent,
    FaIconComponent,
    FormsModule,
    TooltipDirective,
    BadgeComponent,
  ],
})
export class AppShieldOverviewPage extends AbstractLazyDirective implements OnDestroy {
  private readonly dialog = inject(BsModalService);
  private readonly viewportScroller = inject(ViewportScroller);

  readonly #route = inject(ActivatedRoute);
  readonly contentService = inject(ContentService);
  readonly shieldService = inject(ShieldService);
  readonly #shieldStore = inject(ShieldApiStore);
  readonly #accessListsStore = inject(ShieldAccessListsApiStore);
  readonly #uploadScanningStore = inject(ShieldUploadScanningApiStore);
  readonly #pullZoneService = inject(PullZoneApiService);
  public userStore = inject(UserStateService).userStore;

  readonly theme$: Observable<UserTheme>;
  readonly selectedShield = this.#shieldStore.selectedEntity();
  readonly dataGranularity = signal<ChartDataGranularity>('daily');
  readonly timeframe = signal<number>(7);

  public datePickerValue: Date[] | null = null;
  public datePickerConfig = {
    adaptivePosition: true,
    dateInputFormat: DAYJS_FORMAT.DatePickerDate,
    rangeInputFormat: DAYJS_FORMAT.DatePickerDate,
    maxDate: DateTimeUtils.dayjsNow().toDate(),
  };

  public lastHourReport = signal<FinalReport | null>(null);
  public isLoadingLastHourReport = signal(false);
  public lastHourReportError = signal<string | undefined>(undefined);
  public lastHourReportProgress = signal<number | null>(null);
  private hasLoadedReport = false;

  public lastHourTopUrls = computed(() => {
    const report = this.lastHourReport();
    if (!report?.Top50UrlsByRequests) return [];
    return Object.entries(report.Top50UrlsByRequests)
      .map(([url, count]) => ({ label: url, value: count }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 50);
  });

  public lastHourTopIPs = computed(() => {
    const report = this.lastHourReport();
    if (!report?.Top50IPRequests) return [];
    return Object.entries(report.Top50IPRequests)
      .map(([ip, count]) => ({ label: ip, value: count }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 50);
  });

  public lastHourTopCountries = computed(() => {
    const report = this.lastHourReport();
    if (!report?.PerCountry) return [];
    return Object.entries(report.PerCountry)
      .map(([countryCode, count]) => ({ label: countryCode, countryCode, value: count }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 50);
  });

  public lastHourTopUserAgents = computed(() => {
    const report = this.lastHourReport();
    if (!report?.Top50UserAgents) return [];
    return Object.entries(report.Top50UserAgents)
      .map(([userAgent, count]) => ({ label: userAgent, value: count }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 50);
  });

  public isChildHovered = false;
  public totalCleanRequestsLimit = this.#shieldStore.totalCleanRequestsLimit;
  public totalBillableRequests = this.#shieldStore.totalBillableRequests;

  protected readonly PlanType = PlanType;

  pbTitleBadge: PremiumBannerTitle['badge'] = {
    text: '$9.50 per month',
    contentClass: 'text-100-regular',
    size: 'md',
    contextType: 'light',
    placement: 'bottom',
  };

  pbTitle: PremiumBannerTitle = {
    text: 'Bunny Shield Advanced',
    badge: this.userStore.hidePricing() ? undefined : this.pbTitleBadge,
  };

  readonly pbListData: ListWithIconItem[] = [
    {
      icon: 'block-brick-fire',
      title: '255 managed and custom WAF rules',
    },
    {
      icon: 'shield-cross',
      title: '10 rate limit rules',
    },
    {
      icon: 'shield-keyhole',
      title: 'Extensive OWASP top 10',
    },
    {
      icon: 'robot',
      title: 'Complex Bot Mitigation',
    },
    {
      icon: 'microchip-ai',
      title: 'Real-time threat intelligence',
    },
    {
      icon: 'lock',
      title: 'Expert managed DDoS rules',
    },
  ];
  public readonly statisticsListItems: ListWithIconItem[] = [
    {
      icon: 'circle-check',
      text: '0 Total HTTP requests',
      iconContextType: 'warning',
    },
    {
      icon: 'circle-check',
      text: '0 Total triggered rules',
      iconContextType: 'warning',
    },
    {
      icon: 'circle-check',
      text: 'Logging only',
      iconContextType: 'warning',
    },
    {
      icon: 'circle-check',
      text: 'Possible false positives',
      iconContextType: 'warning',
    },
  ];

  public getShield = this.#shieldStore.selectedEntity();
  public shield!: ShieldZoneResponse;
  public logsCount?: number;
  public wafModeTooltipText = '';
  public enabledWafRulesCount = computed(() => {
    return (
      this.#shieldStore.wafAvailableRuleGroups()[this.#shieldStore.selectedEntity()()?.planType || PlanType.Basic].reduce((total, item) => {
        const groupRuleCount = item.ruleGroups?.reduce((groupTotal, group) => {
          return groupTotal + (group.rules || []).length || 0;
        }, 0);
        return total + (groupRuleCount || 0);
      }, 0) +
      (this.#shieldStore.wafCustomRules()?.length || 0) -
      (this.getShield()?.wafLogOnlyRules?.length || 0) -
      (this.getShield()?.wafDisabledRules?.length || 0)
    );
  });
  public titleFirst = 'Documentation';
  public linksFirst?: LinkListItem[] = [
    { text: 'Overview', url: 'https://docs.bunny.net/shield' },
    { text: 'Troubleshooting', url: 'https://docs.bunny.net/shield/troubleshooting' },
    { text: 'API Specifications', url: 'https://api.bunny.net/shield/docs/index.html' },
  ];

  public remainingDays = DateTimeUtils.calculateThisMonthsRemainingDays(true);

  // Note: check how to fill on overview.page.ts
  public wafMode = computed(() => {
    return this.getShield()?.wafExecutionMode === WAFExecutionModeEnum.Logging
      ? WAFExecutionModeEnum[WAFExecutionModeEnum.Logging]
      : WAFExecutionModeEnum[WAFExecutionModeEnum.Blocking];
  });

  public wafModeContextType = computed<ContextType>(() => {
    this.wafModeTooltipText =
      this.wafMode() === WAFExecutionModeEnum[WAFExecutionModeEnum.Logging]
        ? 'WAF is running in <strong>Logging</strong> mode.<br/>Threats are only logged and <strong>not blocked</strong>.'
        : 'WAF is running in <strong>Blocking</strong> mode.<br/>Threats are actively <strong>blocked</strong>.';

    return this.wafMode() === WAFExecutionModeEnum[WAFExecutionModeEnum.Logging] ? 'light' : 'danger';
  });

  public ddosSensitivityLevelContextType = computed<ContextType>(() => this.getSensitivityLevelContext(this.ddosSensitivityLevel()));
  public wafSensitivityLevelContextType = computed<ContextType>(() => this.getSensitivityLevelContext(this.wafSensitivityLevel()));
  public wafSensitivityLevel = computed(() => {
    const detection = this.getShield()?.wafEngineConfig?.find((item) => item.name === 'detection_paranoia_level')?.valueEncoded;
    const executing = this.getShield()?.wafEngineConfig?.find((item) => item.name === 'executing_paranoia_level')?.valueEncoded;
    const blocking = this.getShield()?.wafEngineConfig?.find((item) => item.name === 'blocking_paranoia_level')?.valueEncoded;

    if (detection && executing && blocking && detection === executing && detection === blocking) {
      switch (detection) {
        case '1':
          return 'Low';
        case '2':
          return 'Medium';
        case '3':
          return 'High';
        case '4':
        default:
          return 'Extreme';
      }
    }

    return 'Custom';
  });

  public ddosSensitivityLevel = computed(() => {
    const dDoSShieldSensitivity = this.getShield()?.dDoSShieldSensitivity;

    switch (dDoSShieldSensitivity) {
      case 0:
        return 'Lowest';
      case 1:
        return 'Low';
      case 2:
        return 'Medium';
      case 3:
        return 'High';
      default:
        return 'Extreme';
    }
  });

  public rateLimitingRulesCount = computed(() => this.#shieldStore.rateLimits().length);

  public botDetection = this.#shieldStore.botDetection;
  public botDetectionSensitivity = computed(() => {
    const ipLevel = this.botDetection()?.ipAddressLevel;
    const reqLevel = this.botDetection()?.requestIntegrityLevel;
    const maxLevel = Math.max(ipLevel || 0, reqLevel || 0);
    switch (maxLevel) {
      case 0:
        return 'Low';
      case 1:
        return 'Medium';
      case 2:
        return 'High';
      case 3:
        return 'Extreme';
      default:
        return 'Low';
    }
  });

  public botDetectionMode = computed(() => {
    const mode = this.botDetection()?.executionMode;
    return mode === 1 ? 'Challenge' : 'Logging';
  });

  public accessListsCuratedCount = computed(() => {
    const managed = this.#accessListsStore.managedLists?.();
    return managed?.filter((list) => list.isEnabled).length || 0;
  });

  public accessListsCustomCount = computed(() => {
    const custom = this.#accessListsStore.customLists?.();
    return custom?.length || 0;
  });

  public uploadScanningActiveFilters = computed(() => {
    const csam = this.#uploadScanningStore.csamScanningMode?.();
    const antivirus = this.#uploadScanningStore.antivirusScanningMode?.();
    let count = 0;
    if (csam && csam > 0) count++;
    if (antivirus && antivirus > 0) count++;
    return count;
  });

  public ddosMiniChartSeries?: StringNumberMap;
  public ddosMiniChartValues: (number | undefined)[] = [undefined, undefined];
  public rateLimitingMiniChartSeries?: StringNumberMap;
  public rateLimitingMiniChartValues: (number | undefined)[] = [undefined, undefined];
  public trafficChart = signal<ShieldOverviewChartData>(MapShieldOverviewChartsData());
  public trafficChartHasData = signal(false);
  public isLoadingDetailedChartMetrics = this.#shieldStore.isLoading(StoreRequest.Item, ['detailedChartMetrics']);
  public wafMiniChartSeries?: StringNumberMap;
  public wafMiniChartValues: (number | undefined)[] = [undefined, undefined];
  public botDetectionMiniChartSeries?: StringNumberMap;
  public botDetectionMiniChartValues: (number | undefined)[] = [undefined, undefined];
  public accessListMiniChartSeries?: StringNumberMap;
  public accessListMiniChartValues: (number | undefined)[] = [undefined, undefined];
  public uploadScanningMiniChartSeries?: StringNumberMap;
  public uploadScanningMiniChartValues: (number | undefined)[] = [undefined, undefined];
  public showPricingTable = signal(false);
  public openRows: StringBooleanMap = {};
  public shieldOverviewDay!: number;

  public onRowToggle(index: number): void {
    this.openRows[index] = !this.openRows[index] || false;
  }

  constructor() {
    super();

    this.contentService.setTitle('Overview');
    this.contentService.setBackButton({
      routerLink: `/cdn`,
    });

    this.theme$ = inject(ThemeService).getCurrentTheme();

    this.subscribeToStoreSignals();
    this.subscribeToRouteChanges();

    effect(() => {
      const shield = this.selectedShield();
      if (this.hasLoadedReport) {
        return;
      }

      if (shield?.pullZoneId) {
        this.hasLoadedReport = true;
        setTimeout(() => {
          this.getLastHourReport();
          this.loadLast24HoursMetrics();
        });
      }
    });
  }

  public ngOnDestroy(): void {
    this.contentService.setBackButton(null);
    this.contentService.setBadge(null);
  }

  public imUnderAttackClick(): void {
    const confirmDialogRef = this.dialog.show(ConfirmationDialogComponent, {
      class: 'modal-dialog-centered modal-sm',
      initialState: {
        data: {
          contextType: 'danger',
          confirmButtonName: 'Exit Learning Mode',
          description:
            'You are currently in <strong>learning mode</strong> this action will forcibly enable <strong>blocking mode</strong>. <br/>Are you sure you want to continue?',
          event: {
            action: 'confirm',
          },
          title: 'Exit learning mode',
          textCenter: true,
        },
      },
    });

    this.subscriptions.add(
      confirmDialogRef.content?.getOnAction$().subscribe((event) => {
        if (event.action === 'cancel') {
          this.dialog.hide();
        }
        if (event.action === 'confirm') {
          const id = this.getShield()?.shieldZoneId;
          if (!id) return;

          this.#shieldStore.updateShield({
            id,
            data: { learningMode: false, wafExecutionMode: WAFExecutionModeEnum.Blocking },
          });
        }
      }),
    );
  }

  private subscribeToRouteChanges(): void {
    this.#route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      if (params.get('compare') === 'true') this.handleComparePlans();
    });
  }

  private subscribeToStoreSignals(): void {
    effect(() => {
      const shield = this.getShield();

      if (!shield) return;

      if (this.shield && this.shield.planType !== shield.planType) {
        this.showPricingTable.set(false);
      }

      if (shield.learningModeUntil) {
        this.getShieldSetupDay(shield.learningModeUntil);
        this.viewportScroller.scrollToPosition([0, 0]);
      }

      const plan = shield?.planType || 0;

      if (plan > PlanType.Basic) {
        const planName = PlanType[plan];

        this.contentService.setBadge({
          contextType: 'premium',
          text: planName,
          tooltip: `You have Bunny Shield ${planName} enabled`,
        });
      } else {
        this.contentService.setBadge(null);
      }

      this.shield = shield;
    });

    toObservable(this.#shieldStore.detailedChartMetrics)
      .pipe(takeUntilDestroyed())
      .subscribe((detailedChartMetrics) => {
        this.trafficChart.set(detailedChartMetrics.overviewChartData);
        this.trafficChartHasData.set(detailedChartMetrics.overviewChartData.hasData || false);
      });

    toObservable(this.#shieldStore.last24HoursChartMetrics)
      .pipe(takeUntilDestroyed())
      .subscribe((chartMetrics) => {
        // Store mini chart series for Last 24 hours cards
        this.wafMiniChartSeries = chartMetrics.wafMiniChartSeries;
        this.ddosMiniChartSeries = chartMetrics.ddosMiniChartSeries;
        this.rateLimitingMiniChartSeries = chartMetrics.rateLimitingMiniChartSeries;
        this.botDetectionMiniChartSeries = chartMetrics.botDetectionMiniChartSeries;
        this.accessListMiniChartSeries = chartMetrics.accessListMiniChartSeries;
        this.uploadScanningMiniChartSeries = chartMetrics.uploadScanningMiniChartSeries;

        // Take last 2 values from each for percent comparison
        this.wafMiniChartValues = Object.values(chartMetrics.wafMiniChartSeries).slice(-2);
        this.ddosMiniChartValues = Object.values(chartMetrics.ddosMiniChartSeries).slice(-2);
        this.rateLimitingMiniChartValues = Object.values(chartMetrics.rateLimitingMiniChartSeries).slice(-2);
        this.botDetectionMiniChartValues = Object.values(chartMetrics.botDetectionMiniChartSeries).slice(-2);
        this.accessListMiniChartValues = Object.values(chartMetrics.accessListMiniChartSeries).slice(-2);
        this.uploadScanningMiniChartValues = Object.values(chartMetrics.uploadScanningMiniChartSeries).slice(-2);
      });
  }

  public getShieldSetupDay(learningModeUntil: string): void {
    const learningUntil = new Date(learningModeUntil);
    const DAY = 1000 * 60 * 60 * 24;
    const WEEK = DAY * 7;
    const learningModeDuration = WEEK;
    const creationDate = learningUntil.getTime() - learningModeDuration;
    const todayDate = new Date().getTime();
    const diffInMs = Math.abs(todayDate - creationDate);
    const diffInDays = Math.floor(diffInMs / DAY);

    this.shieldOverviewDay = diffInDays;
  }

  public onCountUpdate(count: number): void {
    this.logsCount = count;
  }

  public handleComparePlans(): void {
    if (this.showPricingTable()) {
      this.showPricingTable.set(false);
    } else {
      this.showPricingTable.set(true);

      setTimeout(() => {
        const el = document.getElementById('appShieldOverviewPricingTable');

        if (el) {
          // Note: wait for animation to finish
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }

  public handleSelectShieldPackage(shieldPackage: PlanType): void {
    this.chagePlan(shieldPackage);
  }

  public chagePlan(planType: PlanType): void {
    this.shieldService.changePlan(planType);
  }

  public onDatePickerChange(dates: Date[] | null): void {
    this.datePickerValue = dates;
    const shieldId = this.selectedShield()?.shieldZoneId;

    if (!shieldId) return;

    if (dates?.length === 2) {
      // Convert inputs to Dayjs objects
      let dateFrom = dayjs(dates.at(0));
      const dateTo = dayjs(dates.at(1));

      if (dateFrom.isSame(dateTo, 'day')) {
        dateFrom = dateFrom.subtract(1, 'day');
      }

      this.datePickerValue = [dateFrom.toDate(), dateTo.toDate()];

      const daysDiff = dateTo.diff(dateFrom, 'day');
      this.timeframe.set(daysDiff);
      this.dataGranularity.set(daysDiff <= 1 ? 'hourly' : 'daily');

      this.#shieldStore.readDetailedChartMetrics({ id: shieldId, dateTimeFrom: DateTimeUtils.dayjs(dateFrom), dateTimeTo: DateTimeUtils.dayjs(dateTo) });
    }
  }

  private loadLast24HoursMetrics(): void {
    const shieldId = this.selectedShield()?.shieldZoneId;
    if (!shieldId) return;

    const now = DateTimeUtils.dayjsNow();
    const sevenDaysAgo = now.subtract(7, 'days');
    const dateTimeFrom = DateTimeUtils.toLocalTime(sevenDaysAgo.toDate());
    const dateTimeTo = DateTimeUtils.toLocalTime(now.toDate());

    this.datePickerValue = [sevenDaysAgo.toDate(), now.toDate()];
    this.#shieldStore.readDetailedChartMetrics({ id: shieldId, dateTimeFrom, dateTimeTo });
    this.#shieldStore.readLast24HoursMetrics({ id: shieldId });
  }

  public refreshDetailedChartMetrics(): void {
    const shieldId = this.selectedShield()?.shieldZoneId;
    if (!shieldId) return;

    const dates = this.datePickerValue;
    if (dates?.length === 2) {
      const dateTimeFrom = DateTimeUtils.dayjs(dates[0]);
      const dateTimeTo = DateTimeUtils.dayjs(dates[1]);
      this.#shieldStore.readDetailedChartMetrics({ id: shieldId, dateTimeFrom, dateTimeTo });
    } else {
      const now = DateTimeUtils.dayjsNow();
      const sevenDaysAgo = now.subtract(7, 'days');
      const dateTimeFrom = DateTimeUtils.toLocalTime(sevenDaysAgo.toDate());
      const dateTimeTo = DateTimeUtils.toLocalTime(now.toDate());
      this.#shieldStore.readDetailedChartMetrics({ id: shieldId, dateTimeFrom, dateTimeTo });
    }
  }

  private readonly replayRequestWhileProcessingDelay = 5000;

  public getLastHourReport(): void {
    const pullZoneId = this.selectedShield()?.pullZoneId;
    if (!pullZoneId) {
      this.lastHourReportError.set(undefined);
      return;
    }

    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const year = String(today.getFullYear() % 100).padStart(2, '0');
    const dateStr = `${month}-${day}-${year}`;

    this.isLoadingLastHourReport.set(true);
    this.lastHourReportError.set(undefined);
    this.lastHourReportProgress.set(null);

    this.subscriptions.add(
      timer(0, this.replayRequestWhileProcessingDelay)
        .pipe(
          exhaustMap(() => this.#pullZoneService.getPullZoneReport(pullZoneId, dateStr, true)),
          tap((data) => {
            this.lastHourReportProgress.set(data.Progress);
          }),
          skipWhile(({ Status, FinalReport: report }) => Status !== 'success' && !report),
          first(),
        )
        .subscribe({
          next: ({ FinalReport: report }) => {
            this.lastHourReport.set(report);
            this.isLoadingLastHourReport.set(false);
          },
          error: () => {
            this.lastHourReportError.set(undefined);
            this.isLoadingLastHourReport.set(false);
          },
        }),
    );
  }

  public refreshLastHourReport(): void {
    this.hasLoadedReport = true;

    const pullZoneId = this.selectedShield()?.pullZoneId;
    if (!pullZoneId) return;

    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const year = String(today.getFullYear() % 100).padStart(2, '0');
    const dateStr = `${month}-${day}-${year}`;

    this.isLoadingLastHourReport.set(true);
    this.lastHourReportProgress.set(null);

    this.subscriptions.add(
      this.#pullZoneService.refreshPullZoneReport(pullZoneId, dateStr, true).subscribe({
        next: (response) => {
          if (response?.Details?.includes('refresh')) {
            this.toastr.warning(response.Details);
            this.isLoadingLastHourReport.set(false);
            return;
          }
          this.getLastHourReport();
        },
        error: () => {
          this.lastHourReportError.set(undefined);
          this.isLoadingLastHourReport.set(false);
        },
      }),
    );
  }

  private getSensitivityLevelContext(level: ReturnType<typeof this.ddosSensitivityLevel | typeof this.wafSensitivityLevel>): ContextType {
    switch (level) {
      case 'Lowest':
        return 'light-2';
      case 'Low':
        return 'light';
      case 'Medium':
        return 'info';
      case 'High':
        return 'warning';
      case 'Extreme':
        return 'danger';
      default:
        return 'light-2';
    }
  }

  SHIELD_PATHS = SHIELD_PATHS;
}
